import { THEME_EDITOR_FLAG, THEME_EDITOR_THEME } from '@xshop/shared';
import { isThemeSlug, type ThemeSlug } from '../themeHost/themeMount';
import { elementAt } from './editorElement';

// What tells this storefront it is being drawn inside the theme builder rather than visited.
//
// A builder preview is the merchant's own shop — their catalogue, their prices, their saved edits —
// wearing one of their installed themes. That is why it is spelled with its own two keys instead of
// `?theme=`, which names a demo shop a merchant is trying a theme on before they own it.

// Reports whether this window is drawn inside another one, which a builder preview always is.
const isFramed = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

// Reads the theme the builder is editing, or null when this visit is not a builder preview at all.
export function editorThemeSlug(search?: string): ThemeSlug | null {
  const params = new URLSearchParams(search ?? window.location.search);
  if (params.get(THEME_EDITOR_FLAG) !== '1') return null;
  const slug = params.get(THEME_EDITOR_THEME);
  return isThemeSlug(slug) ? slug : null;
}

/**
 * Reports whether the address is a builder address. The chrome that only makes a page editable —
 * marking its sections, letting the frame scroll — keys off this, so the storefront can be opened at
 * a builder address directly and behave the same as it does inside the builder.
 */
export const isEditorAddress = (search?: string): boolean => editorThemeSlug(search) !== null;

/**
 * Reports whether this visit is the builder's preview: a builder address, and a frame to talk to.
 * The session keys off this rather than the address alone, because it swallows every click on the
 * page — a visitor who arrived carrying the flag must still be able to use the shop.
 */
export const isThemeEditor = (search?: string): boolean => isEditorAddress(search) && isFramed();

/** The window that opened this preview, so a message is addressed to the builder rather than broadcast. */
export const builderOrigin = (): string => {
  try {
    return document.referrer ? new URL(document.referrer).origin : '*';
  } catch {
    return '*';
  }
};

/** The smallest marked thing under a point: the innermost node of the element tree, or nothing. */
export type EditorMark = { elementId: string | null };

/** Nothing marked — what a point off the page, or on the builder's own chrome, resolves to. */
export const NO_MARK: EditorMark = { elementId: null };

// Resolves what a point in the page is: the innermost node under it, which is the finest thing a
// merchant owns, so a click at any depth selects exactly what the pointer is on.
export const markAt = (target: EventTarget | null): EditorMark => ({ elementId: elementAt(target) });

// Turns an id the builder named into a mark, without asking whether the page has drawn it yet. It used
// to answer NO_MARK for an id with no node under it — and the node an element has just been added as
// arrives in the same tick as the `select` that names it, one message ahead, so the page had not drawn
// it when this ran and the selection was dropped on the floor. A merchant saw the rail open on their new
// element and the canvas show nothing picked. The outline measures its box every frame and draws
// nothing while there is nothing to measure, so a mark for a node not drawn yet costs nothing to keep.
export const markFromId = (id: string | null): EditorMark => (id ? { elementId: id } : NO_MARK);

// Reports whether two resolved marks name the same thing, so an outline only moves when the pointer does.
export const sameMark = (a: EditorMark, b: EditorMark): boolean => a.elementId === b.elementId;

/** The class every piece of the builder's own chrome carries, so the canvas never mistakes it for the page. */
export const CHROME_CLASS = 'xs-editor-chrome';

/** The class the document wears while x-ray is on, which is what outlines every box of the page at once. */
export const XRAY_CLASS = 'xs-editor-xray';

/**
 * The class chrome wears when it is drawn *for* whatever the pointer is on rather than over the page in
 * general. Reaching for the `+` offered on a node means moving the pointer onto that `+`, and without
 * this the hover would clear on the way — taking the very button being reached for off the screen first.
 */
export const HOLD_CLASS = 'xs-editor-hold';

// Reports whether a point is on the builder's own chrome rather than on the storefront drawn under it.
export const isChrome = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest(`.${CHROME_CLASS}`) !== null;

// Reports whether a point is on chrome standing for the hovered thing, which pointing at is not leaving it.
export const holdsHover = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest(`.${HOLD_CLASS}`) !== null;
